import { useEffect, useMemo, useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ArrowLeft, Printer, FileSpreadsheet } from "lucide-react";

const BASE_URL = "https://nissi-india.com";

const monthName = (m) => {
  const names = [
    "",
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const n = parseInt(m, 10);
  return names[n] || "";
};

const DailyAttendanceDetails = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const location = useLocation();
  const qs = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const finYearId = localStorage.getItem("finYearId") || "";
  const branchId = localStorage.getItem("branchid") || localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchtype") || localStorage.getItem("branchType") || "HO";
  const locationName = localStorage.getItem("location") || "";

  // query params from handleDetails()
  const empType = qs.get("empType") || "working";
  const res = empType === "resigned" ? "1" : "0";

  const payload = useMemo(
    () => ({
      finYearId: Number(finYearId),
      branchType,
      branchId,
      location: locationName,

      empcode: (qs.get("empcode") || "").trim(),
      empname: (qs.get("empname") || "").trim(),
      designation: (qs.get("designation") || "").trim(),
      branch: (qs.get("branch") || "").trim(),

      fromdate: (qs.get("fromDate") || "").trim(), // keep as yyyy-mm-dd (node can convert)
      todate: (qs.get("toDate") || "").trim(),

      month: (qs.get("month") || "").trim(),
      year: (qs.get("year") || "").trim(),

      res,
    }),
    [finYearId, branchType, branchId, locationName, qs, res]
  );

  const [loading, setLoading] = useState(false);
  const [header, setHeader] = useState({ branch: "", month: "", monthName: "", year: "" });
  const [keys, setKeys] = useState([]);
  const [rows, setRows] = useState([]);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-43/2", []);

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-5 py-2 rounded-2xl text-white font-bold shadow ` +
    `transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-5 py-2 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark ? "border-white/10 text-white hover:bg-white/10" : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const fetchData = async () => {
    if (!payload.finYearId) return toast.warn("Financial year missing");
    if (!payload.branchId || !payload.branchType) return toast.warn("Branch info missing");

    try {
      setLoading(true);
      const resp = await axios.get(`${BASE_URL}/api/daily-attendance-details/data`, { params: payload });
      if (resp?.data?.success) {
        setHeader(resp.data.header || {});
        setKeys(resp.data.keys || []);
        setRows(resp.data.rows || []);
      } else {
        toast.error(resp?.data?.message || "Failed to load details");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Details error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBack = () => {
    // if opened in new tab
    try {
      window.close();
    } catch {}
    navigate("/payroll/daily-attendance-report");
  };

  const handleExcel = async () => {
    try {
      setLoading(true);

      const resp = await axios.get(`${BASE_URL}/api/daily-attendance-details/excel`, {
        params: payload,
        responseType: "blob",
      });

      const blob = new Blob([resp.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const mm = header?.month || payload.month || "";
      const yy = header?.year || payload.year || "";
      a.href = url;
      a.download = `AttendanceSheet_${mm}_${yy}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Excel download error");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    // just reuse current querystring so no missing variables issue
    window.open(`/PayRoll/DailyAttendanceDetailsPrint${location.search}`, "_blank");
  };

  // helper to color cells like ASP
  const cellClass = (val) => {
    const v = String(val ?? "").toUpperCase();
    if (v === "L" || v === "A") return "text-red-500 font-bold";
    if (v === "M") return "text-pink-500 font-bold";
    if (v === "M1") return "text-green-600 font-bold";
    return "";
  };

  const getCellText = (val) => {
    const v = String(val ?? "");
    return v === "M1" ? "M" : v;
  };

  // show only useful columns in a nice order if keys contain them
  const orderedKeys = useMemo(() => {
    if (!Array.isArray(keys) || keys.length === 0) return [];

    const set = new Set(keys);

    const base = ["fld_Employeecode", "fld_employee", "fld_designationname"];
    const days = Array.from({ length: 31 }, (_, i) => String(i + 1));
    const tail = ["DW", "H", "LOP", "CLOP"];

    const order = [...base, ...days, ...tail];

    // keep only existing ones
    const final = order.filter((k) => set.has(k));

    // if API has other keys not in our order, append them at end
    const rest = keys.filter((k) => !final.includes(k));

    return [...final, ...rest];
  }, [keys]);

  const displayBranch = header.branch || payload.branch || "-";
  const displayMonth = header.monthName || monthName(header.month || payload.month) || "-";
  const displayYear = header.year || payload.year || "-";

  const headerLabel = (k) => {
    if (k === "fld_Employeecode") return "Employee Code";
    if (k === "fld_employee") return "Employee Name";
    if (k === "fld_designationname") return "Designation";
    return k; // days => "1", "2", ..., tail => "H", "LOP"
  };

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">DAILY ATTENDANCE DETAILS</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <div className="text-2xl font-extrabold">Daily Attendance Report</div>
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>{screenBadge}</div>
            </div>

            <div className="flex flex-wrap gap-3 justify-end">
              <button onClick={handleBack} className={ghostBtn}>
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              <button onClick={handleExcel} className={ghostBtn} disabled={loading}>
                <FileSpreadsheet className="w-4 h-4" />
                Excel
              </button>

              <button onClick={handlePrint} className={primaryBtn}>
                <Printer className="w-4 h-4" />
                Print
              </button>
            </div>
          </div>

          {/* Branch/Month/Year */}
          <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 mb-5 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
            <div className="font-bold">
              Branch : <span className="font-extrabold">{displayBranch}</span>
            </div>
            <div className="font-bold">
              Month : <span className="font-extrabold">{displayMonth}</span>
            </div>
            <div className="font-bold">
              Year : <span className="font-extrabold">{displayYear}</span>
            </div>
          </div>

          {/* Table */}
          <div className={`rounded-3xl border shadow-xl overflow-hidden ${isDark ? "border-white/10 bg-white/5" : "border-gray-200 bg-white"}`}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-3 text-center font-bold">S.No</th>
                    {orderedKeys.map((k) => (
                      <th key={k} className="px-3 py-3 text-center font-bold whitespace-nowrap">
                        {headerLabel(k)}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td colSpan={orderedKeys.length + 1} className="px-4 py-12 text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={orderedKeys.length + 1} className="px-4 py-12 text-center">
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        <td className="px-4 py-3 text-center font-bold">{idx + 1}</td>

                        {orderedKeys.map((k) => (
                          <td key={k} className={`px-3 py-3 text-center whitespace-nowrap ${cellClass(r[k])}`}>
                            {getCellText(r[k])}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className={`mt-4 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
            Total: <b>{rows.length}</b>
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default DailyAttendanceDetails;